import { queryHtmlElement } from "../core/dom";

const MOBILE_NAVIGATION_MEDIA_QUERY = "(max-width: 61.9375rem)";
const FOCUSABLE_SELECTOR =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), " +
  "textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";
const SHELL_LAYOUT_CHANGE_EVENT = "lm:shell-layout-change";
const COMPACT_SHELL_MEDIA_QUERY = "(min-width: 62rem)";
const FLYOUT_VIEWPORT_GUTTER_PX = 16;
const FLYOUT_BUTTON_GAP_PX = 14;
const FLYOUT_MIN_HEIGHT_PX = 120;

interface ShellFlyoutMenu {
  menu: HTMLElement;
  panel: HTMLElement;
  toggle: HTMLElement;
}

/**
 * Manages the left-hand application shell so it can collapse cleanly on
 * narrow screens while remaining persistent on desktop layouts.
 */
export class ApplicationShellController {
  private readonly compactMediaQuery = window.matchMedia(COMPACT_SHELL_MEDIA_QUERY);
  private readonly dismissButton: HTMLElement | null;
  private readonly flyoutMenus: ShellFlyoutMenu[];
  private keyboardDismissedFlyout = false;
  private readonly main: HTMLElement | null;
  private readonly mobileMediaQuery = window.matchMedia(MOBILE_NAVIGATION_MEDIA_QUERY);
  private readonly navControls: HTMLElement[];
  private readonly root: HTMLElement | null;
  private readonly sidebar: HTMLElement | null;
  private readonly toggleButton: HTMLElement | null;

  public constructor(container: HTMLElement) {
    this.main = queryHtmlElement(container, "[data-shell-main]", HTMLElement);
    this.root = queryHtmlElement(container, "[data-shell-root]", HTMLElement);
    this.sidebar = queryHtmlElement(container, "[data-shell-sidebar]", HTMLElement);
    this.toggleButton = queryHtmlElement(container, "[data-shell-toggle]", HTMLElement);
    this.dismissButton = queryHtmlElement(
      container,
      "[data-shell-dismiss]",
      HTMLElement
    );
    this.navControls = Array.from(container.querySelectorAll<HTMLElement>("[data-shell-nav-link]"));
    this.flyoutMenus = Array.from(container.querySelectorAll<HTMLElement>("[data-shell-flyout-menu]"))
      .flatMap((menu) => this.buildFlyoutMenu(menu));
  }

  public connect(): void {
    if (this.root === null) {
      return;
    }

    this.syncViewportHeight();
    this.root.dataset.shellEnhanced = "true";
    this.root.dataset.sidebarOpen = "false";
    this.toggleButton?.addEventListener("click", this.handleToggleClick);
    this.dismissButton?.addEventListener("click", this.handleDismissClick);
    this.mobileMediaQuery.addEventListener("change", this.handleViewportChange);
    this.compactMediaQuery.addEventListener("change", this.handleViewportChange);
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeydown);
    window.addEventListener("resize", this.handleWindowResize);
    this.sidebar?.addEventListener("scroll", this.handleSidebarScroll);

    for (const flyoutMenu of this.flyoutMenus) {
      this.connectFlyoutMenu(flyoutMenu);
    }

    for (const control of this.navControls) {
      control.addEventListener("click", this.handleNavigationClick);
    }

    this.syncSidebarState(false);
  }

  private readonly handleDismissClick = (): void => {
    this.closeFlyoutMenus();
    this.syncSidebarState(false);
  };

  private readonly handleDocumentClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof EventTarget)) {
      return;
    }

    const targetNode = target as Node;
    for (const { menu, panel } of this.flyoutMenus) {
      if (menu.contains(targetNode) || panel.contains(targetNode)) {
        return;
      }
    }

    this.closeFlyoutMenus();
  };

  private readonly handleDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.keyboardDismissedFlyout = true;
      this.closeFlyoutMenus();
      this.keyboardDismissedFlyout = false;
      this.syncSidebarState(false);
    }
  };

  private readonly handleNavigationClick = (): void => {
    this.closeFlyoutMenus();
    this.syncSidebarState(false);
  };

  private readonly handleToggleClick = (): void => {
    this.syncSidebarState(!this.isSidebarOpen());
  };

  private readonly handleViewportChange = (): void => {
    this.syncViewportHeight();
    this.closeFlyoutMenus();
    if (!this.mobileMediaQuery.matches) {
      this.syncSidebarState(false);
    } else {
      this.syncSidebarState(this.isSidebarOpen());
    }
  };

  private readonly handleWindowResize = (): void => {
    this.syncViewportHeight();
    this.positionOpenFlyoutMenus();
  };

  private readonly handleSidebarScroll = (): void => {
    this.positionOpenFlyoutMenus();
  };

  private isSidebarOpen(): boolean {
    return this.root?.dataset.sidebarOpen === "true";
  }

  private syncSidebarState(isOpen: boolean): void {
    if (this.root === null) {
      return;
    }

    const wasOpen = this.isSidebarOpen();
    const shouldOpen = this.mobileMediaQuery.matches && isOpen;
    const shouldHideSidebar = this.mobileMediaQuery.matches && !shouldOpen;
    const shouldDisableMain = this.mobileMediaQuery.matches && shouldOpen;
    this.root.dataset.sidebarOpen = shouldOpen ? "true" : "false";
    this.toggleButton?.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
    this.sidebar?.setAttribute("aria-hidden", shouldHideSidebar ? "true" : "false");
    this.main?.setAttribute("aria-hidden", shouldDisableMain ? "true" : "false");

    if (this.dismissButton !== null) {
      this.dismissButton.hidden = !shouldOpen;
    }

    if (this.sidebar !== null) {
      this.sidebar.inert = shouldHideSidebar;
    }

    if (this.main !== null) {
      this.main.inert = shouldDisableMain;
    }

    if (shouldOpen && !wasOpen) {
      this.focusFirstSidebarControl();
    } else if (!shouldOpen && wasOpen) {
      this.toggleButton?.focus();
    }

    window.dispatchEvent(new Event(SHELL_LAYOUT_CHANGE_EVENT));
  }

  private closeFlyoutMenus(): void {
    for (const { menu, panel, toggle } of this.flyoutMenus) {
      this.hideFlyoutMenu(menu, toggle, panel);
    }
  }

  private buildFlyoutMenu(menu: HTMLElement): ShellFlyoutMenu[] {
    const toggle = menu.querySelector<HTMLElement>("[data-shell-flyout-toggle]");
    const panel = menu.querySelector<HTMLElement>("[data-shell-flyout-panel]");
    if (toggle === null || panel === null) {
      return [];
    }

    return [{ menu, panel, toggle }];
  }

  private connectFlyoutMenu({ menu, panel, toggle }: ShellFlyoutMenu): void {
    this.root?.appendChild(panel);
    toggle.addEventListener("click", (event) => {
      event.preventDefault();
      if (this.isFlyoutPanelOpen(panel)) {
        this.hideFlyoutMenu(menu, toggle, panel);
        return;
      }
      this.openFlyoutMenu(menu, toggle, panel);
    });
  }

  private hideFlyoutMenu(
    menu: HTMLElement,
    toggle: HTMLElement | null,
    panel: HTMLElement | null
  ): void {
    if (panel !== null) {
      const shouldRestoreFocus = this.keyboardDismissedFlyout && this.isFlyoutPanelOpen(panel);
      panel.hidden = true;
      if (shouldRestoreFocus) {
        toggle?.focus();
      }
    }
    menu.dataset.shellFlyoutOpen = "false";
    toggle?.setAttribute("aria-expanded", "false");
  }

  private isFlyoutPanelOpen(panel: HTMLElement): boolean {
    return !panel.hidden;
  }

  private openFlyoutMenu(menu: HTMLElement, toggle: HTMLElement, panel: HTMLElement): void {
    this.closeFlyoutMenus();
    panel.hidden = false;
    this.positionFlyoutMenu(menu, toggle, panel);
    menu.dataset.shellFlyoutOpen = "true";
    toggle.setAttribute("aria-expanded", "true");
    panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
  }

  private positionFlyoutMenu(
    menu: HTMLElement,
    toggle: HTMLElement,
    panel: HTMLElement
  ): void {
    const buttonRect = toggle.getBoundingClientRect();
    const panelWidth = panel.offsetWidth || 248;
    const panelHeight = panel.offsetHeight || FLYOUT_MIN_HEIGHT_PX;
    const opensBelow = menu.dataset.shellFlyoutPlacement === "bottom-end";
    const flyoutGap = opensBelow ? FLYOUT_BUTTON_GAP_PX : 0;
    const preferredLeft = opensBelow
      ? buttonRect.right - panelWidth
      : buttonRect.right + FLYOUT_BUTTON_GAP_PX;
    const maxLeft = window.innerWidth - panelWidth - FLYOUT_VIEWPORT_GUTTER_PX;
    const availableBelow = opensBelow
      ? window.innerHeight - buttonRect.bottom - FLYOUT_VIEWPORT_GUTTER_PX
      : window.innerHeight - buttonRect.top - FLYOUT_VIEWPORT_GUTTER_PX;
    const availableAbove = opensBelow
      ? buttonRect.top - FLYOUT_VIEWPORT_GUTTER_PX
      : buttonRect.bottom - FLYOUT_VIEWPORT_GUTTER_PX;
    const usableBelow = Math.max(0, availableBelow - flyoutGap);
    const usableAbove = Math.max(0, availableAbove - flyoutGap);
    const preferredTop =
      usableBelow >= panelHeight || usableBelow >= usableAbove
        ? (opensBelow ? buttonRect.bottom + FLYOUT_BUTTON_GAP_PX : buttonRect.top)
        : buttonRect.top - panelHeight - (opensBelow ? FLYOUT_BUTTON_GAP_PX : 0);
    const maxTop = window.innerHeight - panelHeight - FLYOUT_VIEWPORT_GUTTER_PX;
    panel.style.setProperty(
      "--lm-nav-menu-flyout-left",
      `${String(Math.max(FLYOUT_VIEWPORT_GUTTER_PX, Math.min(preferredLeft, maxLeft)))}px`
    );
    panel.style.setProperty(
      "--lm-nav-menu-flyout-top",
      `${String(Math.max(FLYOUT_VIEWPORT_GUTTER_PX, Math.min(preferredTop, maxTop)))}px`
    );
    panel.style.setProperty(
      "--lm-nav-menu-flyout-max-height",
      `${String(Math.max(FLYOUT_MIN_HEIGHT_PX, Math.max(usableBelow, usableAbove)))}px`
    );
  }

  private positionOpenFlyoutMenus(): void {
    for (const { menu, panel, toggle } of this.flyoutMenus) {
      if (this.isFlyoutPanelOpen(panel)) {
        this.positionFlyoutMenu(menu, toggle, panel);
      }
    }
  }

  private focusFirstSidebarControl(): void {
    const focusTarget = this.sidebar?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    focusTarget?.focus();
  }

  private syncViewportHeight(): void {
    document.documentElement.style.setProperty("--lm-app-height", `${String(window.innerHeight)}px`);
  }
}
